import React, { useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { canApprove } from '../utils/permissions';
import { Button } from '@/components/ui/button';

/**
 * Admin-only page to configure the two-level approval pools for Purchase
 * Orders and Sales Invoices.
 *
 * Only users with the `can_approve` permission tick are listed as candidates.
 * Admin picks any subset of them for each pool. Any one approver per level
 * can act on a pending document at that level.
 */
const POOL_KEYS = [
  { key: 'po_l1_approver_ids', label: 'Purchase Order — Level 1', accent: 'border-blue-400' },
  { key: 'po_l2_approver_ids', label: 'Purchase Order — Level 2', accent: 'border-blue-600' },
  { key: 'invoice_l1_approver_ids', label: 'Sales Invoice — Level 1', accent: 'border-emerald-400' },
  { key: 'invoice_l2_approver_ids', label: 'Sales Invoice — Level 2', accent: 'border-emerald-600' },
];

export default function ApprovalSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({
    po_l1_approver_ids: [],
    po_l2_approver_ids: [],
    invoice_l1_approver_ids: [],
    invoice_l2_approver_ids: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get('/users'), api.get('/approval-settings')])
      .then(([usersRes, settingsRes]) => {
        if (cancelled) return;
        setUsers(usersRes.data || []);
        setSettings({
          po_l1_approver_ids: settingsRes.data?.po_l1_approver_ids || [],
          po_l2_approver_ids: settingsRes.data?.po_l2_approver_ids || [],
          invoice_l1_approver_ids: settingsRes.data?.invoice_l1_approver_ids || [],
          invoice_l2_approver_ids: settingsRes.data?.invoice_l2_approver_ids || [],
        });
      })
      .catch((e) => console.error(e))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const eligible = useMemo(() => users.filter(canApprove), [users]);
  const filteredEligible = useMemo(() => {
    if (!search) return eligible;
    const q = search.toLowerCase();
    return eligible.filter((u) =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q),
    );
  }, [eligible, search]);

  const toggle = (poolKey, userId) => {
    setSettings((s) => {
      const cur = s[poolKey] || [];
      const next = cur.includes(userId) ? cur.filter((i) => i !== userId) : [...cur, userId];
      return { ...s, [poolKey]: next };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/approval-settings', settings);
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 2500);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-amber-800 bg-amber-50 m-6 rounded">
        Approval Settings can only be viewed by administrators.
      </div>
    );
  }

  return (
    <div className="w-full px-2 sm:px-4 lg:px-6 py-3 sm:py-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-bold">✅ Approval Settings</h1>
        <Button
          onClick={save}
          disabled={saving || loading}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="save-approval-settings"
        >
          {saving ? 'Saving…' : '💾 Save Settings'}
        </Button>
      </div>

      {savedToast && (
        <div className="mb-3 p-2 bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm rounded">
          ✓ Settings saved.
        </div>
      )}

      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900">
        Configure who can approve Purchase Orders and Sales Invoices.
        Documents require <b>Level 1</b> approval first, then <b>Level 2</b>.
        Until both levels approve, the document shows as <b>DRAFT</b> on screen and on print.
        Only users with the <b>"Can approve documents"</b> tick in their permissions matrix appear in the pool list below.
      </div>

      <input
        type="text"
        placeholder="Filter approvers by name or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-4 w-full md:w-96 border rounded px-3 py-2 text-sm"
        data-testid="approver-search"
      />

      {loading ? (
        <div className="p-6 text-center text-gray-500">Loading…</div>
      ) : eligible.length === 0 ? (
        <div className="p-4 bg-amber-50 border border-amber-300 text-amber-900 rounded">
          ⚠️ No users have the "Can approve documents" permission ticked.
          Open <b>Users → Edit</b> for the users you want to make approvers and
          enable the toggle under <b>Special Actions</b>.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {POOL_KEYS.map((pool) => (
            <div
              key={pool.key}
              className={`border-2 ${pool.accent} rounded-lg p-3 bg-white`}
              data-testid={`pool-${pool.key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">{pool.label}</h3>
                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                  {(settings[pool.key] || []).length} selected
                </span>
              </div>
              <div className="max-h-72 overflow-y-auto pr-1 space-y-1">
                {filteredEligible.map((u) => {
                  const checked = (settings[pool.key] || []).includes(u.id);
                  return (
                    <label
                      key={u.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer border ${
                        checked
                          ? 'bg-blue-50 border-blue-300'
                          : 'border-transparent hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(pool.key, u.id)}
                        className="w-4 h-4 accent-blue-600"
                        data-testid={`pool-${pool.key}-${u.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{u.name}</div>
                        <div className="text-xs text-gray-500 truncate">{u.email} · {u.role}</div>
                      </div>
                    </label>
                  );
                })}
                {filteredEligible.length === 0 && (
                  <div className="text-xs text-gray-500 text-center py-2">No matches.</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
